import { FC } from "react";

import { Box, Stack } from "@mui/material";
import { getField, RouteBuilders } from "@simplyk/common";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { useFormV2Context } from "@/features/FormV2/context/FormV2Context";
import { removeCampaignNavigationSearchParams } from "@/features/FormV2/helpers/campaignUrls";
import { ThemedGeneralFundraiser } from "@/features/FormV2/sharedComponents/ThemedGeneralFundraiser";
import { ThemedTeamFundraiser } from "@/features/FormV2/sharedComponents/ThemedTeamFundraiser";
import { FormBlock } from "@/forms-design-system/FormBlock";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { trpc } from "@/helpers/trpc";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useTranslate } from "@/hooks/useTranslate";
import { useUrlBuilder } from "@/hooks/useUrlBuilder";

interface ThemedHostedByProps {
  isIndividualCampaignInTeamCampaign?: boolean;
  campaignId: string;
  teamId?: string;
}

export const ThemedHostedBy: FC<ThemedHostedByProps> = ({
  isIndividualCampaignInTeamCampaign = false,
  campaignId,
  teamId,
}) => {
  const { locale, isoLocale } = useLocaleContext();
  const urlBuilder = useUrlBuilder();

  const { formCurrency } = useFormV2Context();

  // Get campaign data information
  const { data: campaignObject } = trpc.getCampaignWithVolume.useQuery({ campaignId: campaignId as string });

  const campaignLogoUrl = campaignObject?.logoUrl || undefined;
  const campaignTitle = getField(campaignObject?.donationFormFields, locale)?.title || "";
  const campaignOrganizationName = campaignObject?.organization?.name || "";

  // Get team data information
  const { data: teamData } = trpc.getTeamWithVolume.useQuery(
    { teamId: teamId as string },
    { enabled: Boolean(teamId) }
  );

  const teamLogoUrl = teamData?.logoUrl || undefined;
  const teamTitle = getField(teamData?.donationFormFields, locale)?.title || "";
  const teamVolume = teamData?.formVolume || 0;

  if (!campaignObject) {
    return null;
  }

  if (teamId && !teamData) {
    return null;
  }

  return (
    <StatelessThemedHostedBy
      slots={{
        generalFundraiser: (
          <ThemedGeneralFundraiser
            href={urlBuilder({
              route: RouteBuilders.buildPeerToPeerV2GeneralCampaignLink({ isoLocale, path: campaignObject!.path }),
              searchParams: removeCampaignNavigationSearchParams,
            })}
            imageSrc={campaignLogoUrl || null}
            organizationName={campaignOrganizationName}
            campaignTitle={campaignTitle}
            currency={formCurrency}
          />
        ),
        teamFundraiser: isIndividualCampaignInTeamCampaign ? (
          <ThemedTeamFundraiser
            href={urlBuilder({
              route: RouteBuilders.buildPeerToPeerV2TeamCampaignLink({ isoLocale, path: teamData!.path }),
              searchParams: removeCampaignNavigationSearchParams,
            })}
            imageSrc={teamLogoUrl || null}
            campaignTitle={teamTitle}
            totalRaised={teamVolume}
            currency={formCurrency}
          />
        ) : null,
      }}
    />
  );
};

type StatelessThemedHostedByProps = {
  slots: {
    generalFundraiser: React.ReactNode;
    teamFundraiser?: React.ReactNode;
  };
};

export const StatelessThemedHostedBy = ({ slots }: StatelessThemedHostedByProps) => {
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();
  const showHostedByTeam = !!slots.teamFundraiser;

  return (
    <FormBlock variant="card" fullWidth>
      <Stack direction="column" spacing={1}>
        <FormTypography variant="subtitle1" vibe="supershy">
          {t("donationForm", "hostedBy.title")}
        </FormTypography>
        {showHostedByTeam ? (
          <Stack direction={{ md: "row" }} gap={2} display="flex" justifyContent="stretch">
            <Box flex={1} sx={{ minWidth: 0, maxWidth: isSmallScreen ? "100%" : "50%" }}>
              <FormTypography variant="subtitle1" vibe="moderate" marginBottom={1}>
                {t("donationForm", "hostedBy.mainCampaign")}
              </FormTypography>
              {slots.generalFundraiser}
            </Box>
            <Box flex={1} sx={{ minWidth: 0, maxWidth: isSmallScreen ? "100%" : "50%" }}>
              <FormTypography variant="subtitle1" vibe="moderate" marginBottom={1}>
                {t("donationForm", "hostedBy.team")}
              </FormTypography>
              {slots.teamFundraiser}
            </Box>
          </Stack>
        ) : (
          slots.generalFundraiser
        )}
      </Stack>
    </FormBlock>
  );
};
